/* eslint-disable camelcase */
import { type FC } from "react";
import { useSWRPostData } from "@root/shared/hooks/useSWRPostData";
import { Pagination } from "@root/shared/components/Pagination";
import { type SiteId } from "@root/shared/types/type";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- 表示幅の型として typeof 参照でのみ使用する
const MIN_WINDOW_SIZE = 2;
const MAX_WINDOW_SIZE = 4;

// API リクエストは常に最大値で固定する。表示幅(params.window)に関わらずリクエスト body を1種類にすることで、
// 上部・下部のリクエストが同一キーになり、SWR のグローバルキャッシュで1回に集約される。
const REQUEST_WINDOW_SIZE = MAX_WINDOW_SIZE;

type Props = {
  currentPageNum: number;
  fetchUrl: string;
  params: {
    site_id: SiteId;
    // 現在ページの前後に表示するページ数
    window: typeof MIN_WINDOW_SIZE | typeof MAX_WINDOW_SIZE;
    per: number;
    search_instance_attributes: Record<string, string>;
    current_page_action_controller_params: Record<string, string | number>;
  };
};

export type ResponseProps = {
  page_num: number;
  path: string;
}[];

export type PaginationDataProps = {
  pageNum: number;
  path: string;
}[];

export const SearchPagination: FC<Props> = ({ currentPageNum, fetchUrl, params }) => {
  // window は表示幅。リクエスト body は superset で固定するため分離して扱う。
  const { window: windowCount, ...keyParams } = params;

  // NOTE: 画面上部・下部で別々の React root だが、body(=SWRキー)を window:4 で統一するため、
  //       SWR のグローバルキャッシュで同一キーとなり、API リクエストは1回に集約される。
  const data = useSWRPostData<ResponseProps>(fetchUrl, {
    ...keyParams,
    window: REQUEST_WINDOW_SIZE,
  });

  // NOTE: Rails 側で例外が発生した場合（rescue のエラー応答は useSWRPostData が null を返す）に備えて配列チェックが必要
  if (!Array.isArray(data)) return null;

  // superset で取得したデータから、現在ページとの距離が windowCount 以内のページだけを表示する
  const isWithinWindow = (pageNum: number): boolean =>
    Math.abs(pageNum - currentPageNum) <= windowCount;
  const paginationData: PaginationDataProps = data
    .filter(({ page_num }) => isWithinWindow(page_num))
    .map(({ page_num, path }) => ({ pageNum: page_num, path }));

  if (!paginationData.length) return null;

  return (
    <Pagination
      currentPageNum={currentPageNum}
      paginationData={paginationData}
      windowCount={windowCount}
    />
  );
};
